import { useEffect } from 'react';
import { useSurface } from '@/contexts/SurfaceContext';

/**
 * Bottom share of the window the Windows touch keyboard covers on a 1080 × 1920 portrait kiosk,
 * used when the browser does not tell us (the visual viewport does not shrink).
 */
export const KIOSK_KEYBOARD_INSET = 0.45;

/**
 * How long the extra bottom room stays after focus leaves the text fields. The tap that blurs a
 * field (mousedown) and its click (mouseup) must land on the same layout: removing the room at
 * once shifts the page under the finger and the click is lost. A new focusin cancels it.
 */
export const KEYBOARD_UNPAD_DELAY_MS = 400;

/** A viewport this much shorter than the full window means the keyboard is open. */
const KEYBOARD_SHRINK_RATIO = 0.85;

/** Inputs that never open the keyboard. */
const NON_TEXT_INPUT_TYPES = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'image', 'file', 'range', 'color', 'hidden']);

/** A field the on-screen keyboard types into: text-like inputs, textareas and contenteditable. */
export function isTextField(target: EventTarget | null): target is HTMLElement {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement) {
    return !NON_TEXT_INPUT_TYPES.has(target.type) && !target.readOnly && !target.disabled;
  }
  if (target instanceof HTMLTextAreaElement) return !target.readOnly && !target.disabled;
  const editable = target.closest('[contenteditable]');
  return editable !== null && editable.getAttribute('contenteditable') !== 'false';
}

export interface VisibleArea {
  /** Window px from the top of the layout viewport. */
  top: number;
  bottom: number;
}

/**
 * The part of the window the keyboard leaves visible:
 * - the visual viewport, when the keyboard shrank it;
 * - the whole window, when the keyboard already resized the window itself;
 * - else everything above a fixed 45 % bottom inset (the touch keyboard overlays the page).
 */
export function visibleArea(win: Window, fullHeight: number): VisibleArea {
  const height = win.innerHeight;
  const vv = win.visualViewport;
  if (vv && vv.height < height * KEYBOARD_SHRINK_RATIO) return { top: vv.offsetTop, bottom: vv.offsetTop + vv.height };
  if (height < fullHeight * KEYBOARD_SHRINK_RATIO) return { top: 0, bottom: height };
  return { top: 0, bottom: height * (1 - KIOSK_KEYBOARD_INSET) };
}

/**
 * The app's own vertical scrollers: the layouts' page `<main>` and `.cosmos-scroll-y` (dialog
 * bodies, the full-screen overlay). They take the extra room even before they overflow; adding
 * it is what lets a field at their end rise.
 */
const APP_VERTICAL_SCROLLER = 'main, .cosmos-scroll-y';

/**
 * The nearest ancestor that scrolls vertically. Browsers compute `overflow-y: auto` for a row that
 * only sets `overflow-x: auto` (chip rows, tabs), so a candidate must also overflow vertically,
 * unless it is one of the app's vertical scrollers.
 */
function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    const { overflowY } = getComputedStyle(p);
    if (overflowY !== 'auto' && overflowY !== 'scroll') continue;
    if (p.scrollHeight > p.clientHeight || p.matches(APP_VERTICAL_SCROLLER)) return p;
  }
  return null;
}

/**
 * Centres `field` in `area`: scroll margins as large as the hidden bands make
 * scrollIntoView({ block: 'center' }) centre it in what is left, in every scrolling ancestor.
 */
function centreInArea(field: HTMLElement, area: VisibleArea, win: Window): void {
  const { scrollMarginTop, scrollMarginBottom } = field.style;
  field.style.scrollMarginTop = `${Math.max(0, area.top)}px`;
  field.style.scrollMarginBottom = `${Math.max(0, win.innerHeight - area.bottom)}px`;
  try {
    field.scrollIntoView({ block: 'center', inline: 'nearest' });
  } finally {
    field.style.scrollMarginTop = scrollMarginTop;
    field.style.scrollMarginBottom = scrollMarginBottom;
  }
}

/**
 * Kiosks (the `keyboardAssist` capability; spec 2026-10-08-kiosk-web-split §6): keeps the focused
 * text field visible above the on-screen keyboard. On focus, and when the visual viewport resizes
 * while a field has focus, a field the keyboard would cover is centred in the visible area. While
 * a field has focus its scroller gets that much extra bottom padding, so a field at the very end of
 * a page can still rise; it is removed KEYBOARD_UNPAD_DELAY_MS after focus leaves the text fields
 * (not at once, so the tap that closed the keyboard still lands). Also asks the browser to
 * resize the viewport for the keyboard (`navigator.virtualKeyboard.overlaysContent = false`).
 * Mounted once (BaseLayout); does nothing on other surfaces.
 */
export function useKioskKeyboardAssist(): void {
  const enabled = useSurface().can('keyboardAssist');

  useEffect(() => {
    if (!enabled) return;

    const vk = (navigator as Navigator & { virtualKeyboard?: { overlaysContent: boolean } }).virtualKeyboard;
    if (vk && 'overlaysContent' in vk) {
      try {
        vk.overlaysContent = false;
      } catch {
        /* read-only in this browser */
      }
    }

    let focused: HTMLElement | null = null;
    let padded: { el: HTMLElement; inline: string } | null = null;
    let unpadTimer: ReturnType<typeof setTimeout> | null = null;
    // The window height without a keyboard (updated on resizes while no field has focus).
    let fullHeight = window.innerHeight;

    const cancelUnpad = () => {
      if (unpadTimer === null) return;
      clearTimeout(unpadTimer);
      unpadTimer = null;
    };

    const unpad = () => {
      cancelUnpad();
      if (!padded) return;
      padded.el.style.paddingBottom = padded.inline;
      padded = null;
    };

    const pad = (field: HTMLElement, inset: number) => {
      const scroller = scrollParent(field);
      if (padded && padded.el !== scroller) unpad();
      if (!scroller || inset <= 0) return;
      if (!padded) padded = { el: scroller, inline: scroller.style.paddingBottom };
      scroller.style.paddingBottom = padded.inline;
      const base = Number.parseFloat(getComputedStyle(scroller).paddingBottom) || 0;
      scroller.style.paddingBottom = `${base + inset}px`;
    };

    const assist = () => {
      const field = focused;
      if (!field || !field.isConnected) return;
      const area = visibleArea(window, fullHeight);
      const rect = field.getBoundingClientRect();
      if (rect.top >= area.top && rect.bottom <= area.bottom) return;
      pad(field, window.innerHeight - area.bottom);
      centreInArea(field, area, window);
    };

    const onFocusIn = (event: FocusEvent) => {
      if (!isTextField(event.target)) return;
      cancelUnpad();
      focused = event.target;
      assist();
    };
    const onFocusOut = (event: FocusEvent) => {
      if (isTextField(event.relatedTarget)) return; // moving to the next field
      focused = null;
      if (!padded) return;
      cancelUnpad();
      unpadTimer = setTimeout(unpad, KEYBOARD_UNPAD_DELAY_MS);
    };
    const onWindowResize = () => {
      if (!focused) fullHeight = window.innerHeight;
      else assist();
    };
    const onViewportResize = () => assist();

    const vv = window.visualViewport;
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    window.addEventListener('resize', onWindowResize);
    vv?.addEventListener('resize', onViewportResize);
    return () => {
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      window.removeEventListener('resize', onWindowResize);
      vv?.removeEventListener('resize', onViewportResize);
      unpad();
    };
  }, [enabled]);
}
